import {Component, ElementRef, ViewChild} from '@angular/core';
import * as jsPDF from 'jspdf';
import * as pdfSupport from 'browser-pdf-support';
import {CvDownloadSectionService} from './model/cv-download-section.service';
import {CvDocument} from './document/cv-document.class';
import {EducationTypeId} from './document/education-entry.class';
import {separatedBy} from '../../shared/utils';

@Component({
    template: require('./cv-download-page.component.html'),
    styles: [require('./cv-download-page.component.scss')]
})
export class CvDownloadPageComponent {

    @ViewChild('previewIframe') iframe: ElementRef;

    model: CvDocument;
    supportsPDFPreview: boolean = false;

    protected _fontSizes = {
        headers: 13,
        mainHeader: 20,
        normal: 11,
    };
    protected _margin: number = 0.5;
    protected _pdfDoc: jsPDF = null;
    protected _verticalOffset: number = this._margin * 1.5;

    constructor(cvdm: CvDocument.InjectedMembers, private readonly _cvService: CvDownloadSectionService) {
        const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        this.supportsPDFPreview = pdfSupport() || isFirefox;
        this._cvService.currentUserCv().subscribe(it => {
            this.model = new CvDocument(cvdm, it!);
            this.model.initialized$.subscribe(() => this._generatePDF());
        });
    }

    private _calculateVerticalPosition(lines, fontSize): void {
        let diff = (lines + 0.5) * fontSize / 72;
        if (this._verticalOffset + diff >= 10.5) {
            this._pdfDoc.addPage();
            this._verticalOffset = this._margin * 1.5;
        } else {
            this._verticalOffset += diff;
        }
    }

    private _translateEducationTypeId(typeId): string {
        let translation = '';
        switch (typeId) {
            case EducationTypeId.PREMEDICAL:
                translation = 'Premedical';
                break;
            case EducationTypeId.ECFMG:
                translation = 'ECFMG';
                break;
            case EducationTypeId.MEDICAL:
                translation = 'Medical';
                break;
            case EducationTypeId.OTHER:
                translation = 'Other';
                break;
            case EducationTypeId.INTERNSHIP:
                translation = 'Internship';
                break;
            case EducationTypeId.RESIDENCY:
                translation = 'Residency';
                break;
            case EducationTypeId.FELLOWSHIP_OR_PRECEPTORSHIP:
                translation = 'Fellowship or Preceptorship';
                break;
            default:
                break;
        }
        return translation;
    }

    private _generatePDF(): void {
        if (this.model) {
            const model = this.model;
            let that = this;
            that._pdfDoc = new jsPDF('p', 'in', 'letter');

            if (model.personalInfo) {
                // Opening text (Personal Details)
                that._pdfDoc.setTextColor('#000000');
                that._pdfDoc.setFontType('bold');
                that._pdfDoc.setFontSize(that._fontSizes.mainHeader);
                that._pdfDoc.text(model.personalInfo.name, that._margin, that._verticalOffset);

                that._pdfDoc.setFontType('normal');
                that._pdfDoc.setFontSize(that._fontSizes.headers);

                // Address 1
                if (model.personalInfo.address1) {
                    that._calculateVerticalPosition(1, that._fontSizes.headers);
                    that._pdfDoc.text(model.personalInfo.address1, that._margin, that._verticalOffset);
                }

                // Address 2
                if (model.personalInfo.address2) {
                    that._calculateVerticalPosition(1, that._fontSizes.headers);
                    that._pdfDoc.text(model.personalInfo.address2, that._margin, that._verticalOffset);
                }

                // Phone numbers
                if (model.personalInfo.cellPhoneNumber || model.personalInfo.homePhoneNumber) {
                    let phonesText = (model.personalInfo.cellPhoneNumber) ? `(Cell) ${model.personalInfo.cellPhoneNumber}` : '';
                    phonesText += (model.personalInfo.homePhoneNumber) ?
                        `${(phonesText !== '' ? ` | ` : '')}(Home) ${model.personalInfo.homePhoneNumber}` : '';
                    if (phonesText.length) {
                        that._calculateVerticalPosition(1, that._fontSizes.headers);
                        that._pdfDoc.text(phonesText, that._margin, that._verticalOffset);
                    }
                }

                // Email address
                if (model.personalInfo.email) {
                    that._calculateVerticalPosition(1, that._fontSizes.headers);
                    that._pdfDoc.text(`Email: ${model.personalInfo.email}`, that._margin, that._verticalOffset);
                }

                // Extra bottom space
                that._calculateVerticalPosition(1, that._fontSizes.headers);

                // Dividing line between header and actual content
                that._pdfDoc.setDrawColor(63, 197, 247)
                    .setLineWidth(1 / 72)
                    .line(that._margin, that._verticalOffset, 8.5 - that._margin, that._verticalOffset);

                // Extra bottom space
                that._calculateVerticalPosition(1, that._fontSizes.headers);
            }

            // Body text color
            that._pdfDoc.setTextColor('#333333');

            // Education
            if (model.education.length) {
                that._pdfDoc.setFontType('bold');
                that._pdfDoc.setFontSize(that._fontSizes.headers);
                that._calculateVerticalPosition(1, that._fontSizes.headers);
                that._pdfDoc.text('EDUCATION', that._margin, that._verticalOffset);
                that._pdfDoc.setFontSize(that._fontSizes.normal);
                that._pdfDoc.setFontType('normal');
                model.education.forEach(function (element) {
                    if (element.type) {
                        that._calculateVerticalPosition(2, that._fontSizes.normal);
                        that._pdfDoc.text(`${that._translateEducationTypeId(element.type)}:`, that._margin, that._verticalOffset);
                        that._pdfDoc.text(`${element.name}`, that._margin * 5, that._verticalOffset);
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        if (element.address) {
                            that._pdfDoc.text(`${element.address}`, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        that._pdfDoc.text(separatedBy(' - ', element.dateFrom, element.dateTo), that._margin * 5, that._verticalOffset);
                    }
                });
            }

            // Work
            if (model.work.length) {
                that._pdfDoc.setFontType('bold');
                that._pdfDoc.setFontSize(that._fontSizes.headers);
                that._calculateVerticalPosition(2, that._fontSizes.headers);
                that._pdfDoc.text('WORK HISTORY', that._margin, that._verticalOffset);
                that._pdfDoc.setFontSize(that._fontSizes.normal);
                that._pdfDoc.setFontType('normal');
                model.work.forEach(function (element) {
                    that._calculateVerticalPosition(1, that._fontSizes.normal);
                    if (element.position) {
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        that._pdfDoc.text(`${element.position}`, that._margin, that._verticalOffset);
                    }
                    if (element.facility) {
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        that._pdfDoc.text(`${element.facility}`, that._margin, that._verticalOffset);
                    }
                    if (element.address) {
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        that._pdfDoc.text(`${element.address}`, that._margin, that._verticalOffset);
                    }
                    if (element.address2) {
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        that._pdfDoc.text(`${element.address2}`, that._margin, that._verticalOffset);
                    }
                    if (element.dateFrom) {
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                        that._pdfDoc.text(`${element.dateFrom} - ${(element.dateTo) ? element.dateTo : '?'}`, that._margin, that._verticalOffset);
                    }
                });
            }

            // Licenses and Certifications
            if (model.licensesAndCertifications) {
                that._pdfDoc.setFontType('bold');
                that._pdfDoc.setFontSize(that._fontSizes.headers);
                that._calculateVerticalPosition(2, that._fontSizes.headers);
                that._pdfDoc.text('LICENSES & CERTIFICATIONS', that._margin, that._verticalOffset);
                that._pdfDoc.setFontSize(that._fontSizes.normal);
                that._pdfDoc.setFontType('normal');
                if (model.licensesAndCertifications.medicalLicenses &&
                    model.licensesAndCertifications.medicalLicenses.length) {
                    that._calculateVerticalPosition(2, that._fontSizes.normal);
                    that._pdfDoc.text('Medical License(s):', that._margin, that._verticalOffset);
                    model.licensesAndCertifications.medicalLicenses.forEach(function (element) {
                        that._pdfDoc.text(`${element.state} (${element.active ? 'Active' : 'Inactive'})`, that._margin * 5, that._verticalOffset);
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                    });
                }

                if (model.licensesAndCertifications.boardAffiliations &&
                    model.licensesAndCertifications.boardAffiliations.length) {
                    that._calculateVerticalPosition(1, that._fontSizes.normal);
                    that._pdfDoc.text('Certifications:', that._margin, that._verticalOffset);
                    model.licensesAndCertifications.boardAffiliations.forEach(function (element) {
                        if (element.name) {
                            that._pdfDoc.text(element.name, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        if (element.certificationDate) {
                            that._pdfDoc.text(`Initial Date: ${element.certificationDate}`, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        if (element.recertificationDate) {
                            that._pdfDoc.text(`Expiration Date: ${element.recertificationDate}`, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        if (element.subspecialties && element.subspecialties.length) {
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                            that._pdfDoc.setFontType('italic');
                            that._pdfDoc.text('Subspecialties:', that._margin * 5, that._verticalOffset);
                            that._pdfDoc.setFontType('normal');
                            element.subspecialties.forEach(function (subspecialty) {
                                that._calculateVerticalPosition(1, that._fontSizes.normal);
                                if (subspecialty.name) {
                                    that._pdfDoc.text(subspecialty.name, that._margin * 5.5, that._verticalOffset);
                                    that._calculateVerticalPosition(1, that._fontSizes.normal);
                                }
                                if (subspecialty.certificationDate) {
                                    that._pdfDoc.text(`Certification Date: ${subspecialty.certificationDate}`, that._margin * 5.5, that._verticalOffset);
                                    that._calculateVerticalPosition(1, that._fontSizes.normal);
                                }
                                if (subspecialty.recertificationDate) {
                                    that._pdfDoc.text(`Recertification Date: ${subspecialty.recertificationDate}`, that._margin * 5.5, that._verticalOffset);
                                    that._calculateVerticalPosition(1, that._fontSizes.normal);
                                }
                            });
                        }
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                    });
                }

                if (model.licensesAndCertifications.otherCertifications &&
                    model.licensesAndCertifications.otherCertifications) {
                    model.licensesAndCertifications.otherCertifications.forEach(function (element) {
                        if (element.name) {
                            that._pdfDoc.text(element.name, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        if (element.completionDate) {
                            that._pdfDoc.text(`Completion Date: ${element.completionDate}`, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        if (element.expirationDate) {
                            that._pdfDoc.text(`Expiration Date: ${element.expirationDate}`, that._margin * 5, that._verticalOffset);
                            that._calculateVerticalPosition(1, that._fontSizes.normal);
                        }
                        that._calculateVerticalPosition(1, that._fontSizes.normal);
                    });
                }

                that._pdfDoc.setFontType('bold');
                that._pdfDoc.setFontSize(that._fontSizes.headers);
                that._calculateVerticalPosition(1, that._fontSizes.headers);
                that._pdfDoc.text('REFERENCES', that._margin, that._verticalOffset);
                that._pdfDoc.setFontSize(that._fontSizes.normal);
                that._pdfDoc.setFontType('normal');
                that._calculateVerticalPosition(2, that._fontSizes.normal);
                that._pdfDoc.text('Will be provided upon request.', that._margin, that._verticalOffset);
            }
            if (that.supportsPDFPreview) {
                let output = null;
                if (navigator.msSaveBlob) {
                    output = that._pdfDoc.output('datauristring');
                } else {
                    output = that._pdfDoc.output('bloburi');
                }
                that.iframe.nativeElement.src = output;
            }

        }
    }

    downloadCV(): void {
        if (this._pdfDoc && this.model) {
            let userName = this.model.personalInfo && this.model.personalInfo.name ?
                this.model.personalInfo.name.replace(/\.|\,/g, '').replace(/\s/g, '_') : 'cv';
            let currentDate = new Date();
            let year = currentDate.getFullYear();
            let month: number | string = currentDate.getMonth() + 1;
            let day: number | string = currentDate.getDate();
            let hour: number | string = currentDate.getHours();
            let min: number | string = currentDate.getMinutes();
            let sec: number | string = currentDate.getSeconds();
            month = (month < 10 ? '0' : '') + month;
            day = (day < 10 ? '0' : '') + day;
            hour = (hour < 10 ? '0' : '') + hour;
            min = (min < 10 ? '0' : '') + min;
            sec = (sec < 10 ? '0' : '') + sec;
            this._pdfDoc.save(`${userName}-${year}-${month}-${day}_${hour}${min}_${sec}.pdf`);
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/cv/download/cv-download-page.component.ts